import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/is-base64-encryption';

const FAQS: Faq[] = [
  { q: 'Is Base64 encryption?', a: 'No. Base64 is a reversible encoding with no secret key — anyone can decode a Base64 string back to its original content in milliseconds using any standard library or online tool.' },
  { q: 'Can Base64 hide sensitive data at all?', a: 'It can only obscure data from a casual glance (obfuscation), not from anyone who bothers to decode it. It provides zero protection against a motivated reader.' },
  { q: 'What should I use instead of Base64 for real security?', a: 'For confidentiality use an encryption algorithm like AES-256-GCM with a securely managed key; for passwords use a password hashing function like bcrypt, scrypt or Argon2 — never encode or even encrypt passwords for storage, hash them.' },
  { q: 'Why do systems like JWT use Base64 if it is not secure?', a: 'JWTs use Base64URL purely to make JSON safely transportable as text in a URL or header. The actual security in a JWT comes from a separate cryptographic signature, not from the Base64 encoding of its payload.' },
];

export default function IsBase64EncryptionPage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="Is Base64 Encryption? No — Here's Why | EasyBase64"
        description="Base64 is not encryption. Learn why Base64 provides zero confidentiality, common mistakes that expose secrets, and what to use instead."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'Is Base64 Encryption? No — Here\'s Why',
            description: 'Why Base64 is an encoding, not encryption, and what to use instead when you actually need security.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Is Base64 Encryption?', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            Is Base64 Encryption? No — Here's Why
          </h1>

          <p className={para}>
            Short answer: no. Base64 is a binary-to-text <em>encoding</em>, not encryption, and it
            provides no confidentiality whatsoever. This is one of the most common security
            misunderstandings in software, and it is worth understanding exactly why.
          </p>

          <h2 className={heading}>Encoding vs. encryption</h2>
          <p className={para}>
            Encoding transforms data into a different representation using a public, well-known
            rule — anyone with the rule (which is always publicly documented, since Base64 is an
            open standard, RFC 4648) can reverse it instantly. Encryption transforms data using a
            secret key such that reversing it is computationally infeasible without that key.
            Base64 has no key at all: <code>{"decode(encode(x)) = x"}</code> for every implementation
            everywhere, with nothing secret involved.
          </p>

          <h2 className={heading}>Try it yourself</h2>
          <p className={para}>
            Paste any Base64 string into the <Link to="/base64-decode" className="underline">decoder</Link>{' '}
            and you'll get the original content back immediately — no password, no key. That is the
            entire proof: if reversing a "protected" value takes zero secret knowledge, it isn't
            protection.
          </p>

          <h2 className={heading}>Where this misunderstanding causes real harm</h2>
          <p className={para}>
            Storing API keys, passwords or personal data as "Base64 encoded" in a config file,
            database column or URL parameter gives a false sense of security — anyone who can read
            that value can trivially recover the original. This has caused real data breaches when
            developers assumed encoding was sufficient protection for credentials in transit or at
            rest.
          </p>

          <h2 className={heading}>What Base64 is actually for</h2>
          <p className={para}>
            Base64's real job is compatibility, not security: making binary data safe to place
            inside text-only channels — HTML/CSS{' '}
            <Link to="/base64-data-uri-guide" className="underline">data URIs</Link>, email
            attachments (<Link to="/base64-for-email-attachments" className="underline">MIME encoding</Link>),
            JSON payloads, and JWT segments (see{' '}
            <Link to="/jwt-base64url-encoding" className="underline">how JWTs use Base64URL</Link>).
            In all of these cases, any real security comes from a separate layer — TLS for
            transport, a cryptographic signature for JWTs, or actual encryption for secrets at rest.
          </p>

          <h2 className={heading}>What to use instead for real security</h2>
          <p className={para}>
            For confidentiality, use a vetted encryption algorithm such as AES-256-GCM with a
            properly managed key (e.g. via a KMS). For passwords, never encode or reversibly
            encrypt them at all — hash them with a purpose-built password hashing function like
            bcrypt, scrypt, or Argon2, which are designed to be slow and salted so they resist
            brute-force attacks even if the hash is leaked.
          </p>
        </article>

        <FaqSection title="Is Base64 Encryption? — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
